import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllShippingCategories,
  createShippingCategory,
  updateShippingCategory,
  deleteShippingCategory,
  getAllShippingCompanies,
  getPricesByCompany,
} from "../../redux/actions";
import { LoadingModal } from "../../components/modal/loading";
import {
  CreateShippingCategoryModal,
  EditShippingCategoryModal,
  DeleteConfirmModal,
} from "../../components/shipping/ShippingModals";
import ManageRatesModal from "../../components/modal/Manageratesmodal";

export const ShippingCategory = () => {
  const dispatch = useDispatch();
  const { shippingCategories, companies, prices, loading, pagination } =
    useSelector((s) => s.shipping);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [manageRatesOpen, setManageRatesOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    dispatch(getAllShippingCategories(page, limit));
  }, [dispatch, page]);

  useEffect(() => {
    dispatch(getAllShippingCompanies());
  }, [dispatch]);

  const handleCompanyChange = (companyId) => {
    dispatch(getPricesByCompany(companyId));
  };

  const handleCreate = async (form) => {
    await dispatch(createShippingCategory(form));

    dispatch(getAllShippingCategories(page, limit));

    setCreateOpen(false);
  };

  const handleUpdate = async (form) => {
    await dispatch(updateShippingCategory(selected.id, form));

    dispatch(getAllShippingCategories(page, limit));

    setEditOpen(false);
    setSelected(null);
  };

  const handleDelete = async () => {
    await dispatch(deleteShippingCategory(selected.id));

    // if current page becomes empty
    if (shippingCategories.length === 1 && page > 1) {
      setPage((p) => p - 1);
    } else {
      dispatch(getAllShippingCategories(page, limit));
    }

    setDeleteOpen(false);
    setSelected(null);
  };

  return (
    <div className="space-y-6">
      {loading && <LoadingModal text="Loading..." />}

      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">
          Shipping Categories
        </h1>
        <button
          onClick={() => setCreateOpen(true)}
          className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
        >
          Add Category
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Slug
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Rates
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Sort
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shippingCategories.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-8 text-center text-slate-400 text-sm"
                >
                  No shipping categories found. Add one to get started.
                </td>
              </tr>
            ) : (
              shippingCategories.map((cat, i) => (
                <tr key={cat.id} className="hover:bg-slate-50 transition">
                  <td className="px-4 py-3 text-slate-500">
                    {(page - 1) * limit + i + 1}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {cat.name}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">
                    {cat.slug}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        setSelected(cat);
                        setManageRatesOpen(true);
                      }}
                      className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200 transition"
                    >
                      {cat.shippingRates?.length || 0} rate
                      {cat.shippingRates?.length !== 1 ? "s" : ""} →
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{cat.sortOrder}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-lg px-2 py-1 text-xs font-semibold ${
                        cat.isActive
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-rose-50 text-rose-600"
                      }`}
                    >
                      {cat.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => {
                        setSelected(cat);
                        setEditOpen(true);
                      }}
                      className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setSelected(cat);
                        setDeleteOpen(true);
                      }}
                      className="rounded-lg bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-100"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination?.totalPages > 1 && (
        <div className="mt-5 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {(page - 1) * limit + 1}-
            {Math.min(page * limit, pagination.total)} of {pagination.total}
          </p>

          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg border px-3 py-2 text-sm disabled:opacity-50"
            >
              Previous
            </button>

            {Array.from({ length: pagination.totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`h-9 w-9 rounded-lg border text-sm ${
                  page === i + 1
                    ? "bg-emerald-500 text-white border-emerald-500"
                    : "hover:bg-slate-100"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              disabled={page === pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border px-3 py-2 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <CreateShippingCategoryModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        companies={companies}
        prices={prices}
        onCompanyChange={handleCompanyChange}
      />
      <EditShippingCategoryModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        onUpdate={handleUpdate}
        shippingCategory={selected}
      />
      <ManageRatesModal
        open={manageRatesOpen}
        onClose={() => {
          setManageRatesOpen(false);
          setSelected(null);
        }}
        category={selected}
      />
      <DeleteConfirmModal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        onConfirm={handleDelete}
        title="Delete Shipping Category"
        message={`Are you sure you want to delete "${selected?.name}"? All attached rates will also be removed.`}
      />
    </div>
  );
};
